import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Stack,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import Refresh from '@mui/icons-material/Refresh';
import ExpandMore from '@mui/icons-material/ExpandMore';
import {
  getPaymentTransactionsMonthly,
  PaymentTransactionMonthlyBlock,
  PaymentTransactionMonthlyItem,
  PaymentTransactionsMonthlyResponse,
} from '../../../services/request/Reports';
import { formatCurrency } from '../../../utils/CurrencyFormat';
import { PageHeader, EmptyState } from '../../../shared-components/ui';

function formatInstant(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return '—';
  }
}

const fmt = (n: number) => formatCurrency(n, 'INR', true);

const MonthlyPaymentTransactionsSummary: React.FC = () => {
  const [data, setData] = useState<PaymentTransactionsMonthlyResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getPaymentTransactionsMonthly();
      const payload = response.data;
      if (payload) {
        setData(payload);
      } else {
        setError('No data returned');
      }
    } catch (e: unknown) {
      const err = e as { message?: string };
      setError(err?.message || 'Failed to load report.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchReport();
  }, [fetchReport]);

  const months = data?.months ?? [];
  const lastIdx = months.length - 1;

  return (
    <Box>
      <PageHeader
        eyebrow="Lending · Reports"
        title="Monthly payment transactions"
        subtitle="Payments linked to active loans, from the first recorded month through today."
        actions={
          <Button variant="contained" startIcon={<Refresh />} onClick={() => void fetchReport()} disabled={loading}>
            Refresh
          </Button>
        }
      />

      {loading && <CircularProgress size={32} sx={{ display: 'block', mb: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {data && !loading && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ columnGap: 2, rowGap: 1 }}>
              <Chip label={`${data.grandTotalTransactionCount} transactions (all months)`} color="primary" variant="outlined" />
              <Chip label={`Received: ${fmt(data.grandTotalAmountReceived)}`} color="success" variant="outlined" />
              <Chip label={`Given: ${fmt(data.grandTotalAmountGiven)}`} color="warning" variant="outlined" />
              <Chip
                label={`Net: ${fmt(data.grandNetCashflow)}`}
                color={data.grandNetCashflow >= 0 ? 'success' : 'error'}
                variant="outlined"
              />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Report date: {data.reportDate} · {data.message}
            </Typography>
          </CardContent>
        </Card>
      )}

      {data && !loading && months.length === 0 && (
        <EmptyState title="No payment transactions" description="No payment transactions for active loans to show." />
      )}

      {data &&
        !loading &&
        months.map((m: PaymentTransactionMonthlyBlock, i: number) => (
          <Accordion key={`${m.year}-${m.month}`} defaultExpanded={i === lastIdx} disableGutters sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                flexWrap="wrap"
                useFlexGap
                sx={{ width: '100%', pr: 1 }}
              >
                <Typography fontWeight={600}>{m.monthLabel}</Typography>
                <Chip size="small" label={`${m.transactionCount} txns`} />
                <Chip size="small" color="success" variant="outlined" label={`In: ${fmt(m.totalAmountReceived)}`} />
                <Chip size="small" color="warning" variant="outlined" label={`Out: ${fmt(m.totalAmountGiven)}`} />
                <Chip size="small" variant="outlined" label={`Volume: ${fmt(m.totalMonthlyVolume)}`} />
                <Chip
                  size="small"
                  color={m.netCashflow >= 0 ? 'success' : 'error'}
                  label={`Net: ${fmt(m.netCashflow)}`}
                />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              {m.items.length === 0 ? (
                <Typography color="text.secondary" variant="body2">
                  No transactions this month.
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Customer</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Payment type</TableCell>
                        <TableCell>Received / given</TableCell>
                        <TableCell>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {m.items.map((row: PaymentTransactionMonthlyItem) => (
                        <TableRow key={row.id} hover>
                          <TableCell>{row.customerName}</TableCell>
                          <TableCell align="right">{fmt(row.amount)}</TableCell>
                          <TableCell>{row.paymentTypeLabel}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={row.flow === 'GIVEN' ? 'Given' : 'Received'}
                              color={row.flow === 'GIVEN' ? 'warning' : 'success'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{formatInstant(row.createdAt)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
    </Box>
  );
};

export default MonthlyPaymentTransactionsSummary;
